import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { format, startOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Fish, CalendarDots, ChartLine, FishSimple, Images, SignOut, House,
  Download, FileText, FileCsv, CaretLeft, CaretRight, Receipt, List, X
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInvoicesPage = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/invoices?month=${currentMonth}`, { headers });
      setInvoices(r.data);
    } catch (error) {
      if (error.response?.status === 401) { logout(); navigate('/admin'); }
      else toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  }, [currentMonth, token]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/admin/invoices/${invoiceId}/download`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Facture_${invoiceNumber.replace(/-/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Facture ${invoiceNumber} téléchargée`);
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const exportMonthlyCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/invoices/monthly-export?month=${currentMonth}`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Calvi_Fishing_Factures_${currentMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export CSV du mois téléchargé`);
    } catch {
      toast.error('Erreur lors de l\'export');
    }
  };

  const changeMonth = (direction) => {
    const [year, month] = currentMonth.split('-').map(Number);
    const d = new Date(year, month - 1 + direction, 1);
    setCurrentMonth(format(d, 'yyyy-MM'));
  };

  const monthLabel = (() => {
    const [year, month] = currentMonth.split('-').map(Number);
    return format(new Date(year, month - 1, 1), 'MMMM yyyy', { locale: fr });
  })();

  const totalTTC = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const totalHT = invoices.reduce((sum, inv) => sum + (inv.total_ht || 0), 0);
  const totalTVA = invoices.reduce((sum, inv) => sum + (inv.tva_amount || 0), 0);

  const navItems = [
    { path: '/admin/dashboard', icon: CalendarDots, label: 'Agenda' },
    { path: '/admin/invoices', icon: Receipt, label: 'Factures' },
    { path: '/admin/trips', icon: FishSimple, label: 'Sorties Pêche' },
    { path: '/admin/gallery', icon: Images, label: 'Galerie' },
    { path: '/admin/analytics', icon: ChartLine, label: 'Analytics' },
  ];
  const isCurrentPage = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-invoices">
      {/* Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-[#000000] text-white z-30">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-10 w-10 object-contain rounded-full" />
            <span className="text-lg font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </Link>
        </div>
        <nav className="mt-6 px-3">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${isCurrentPage(item.path) ? 'bg-[#b7ede0] text-[#000000] font-semibold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
              <item.icon size={20} />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2">
          <Link to="/"><Button variant="ghost" className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"><House size={20} className="mr-3" />Voir le site</Button></Link>
          <Button variant="ghost" onClick={() => { logout(); navigate('/admin'); }} className="w-full justify-start text-gray-400 hover:text-[#FF3B30] hover:bg-red-500/10"><SignOut size={20} className="mr-3" />Déconnexion</Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#000000] text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-8 w-8 object-contain rounded-full" />
            <span className="font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </div>
          <Button variant="ghost" size="sm" className="text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </Button>
        </div>
        {mobileMenuOpen && (
          <nav className="mt-3 pb-3 border-t border-white/10 pt-3 space-y-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isCurrentPage(item.path) ? 'bg-[#b7ede0] text-[#000000] font-semibold' : 'text-gray-400'}`}>
                <item.icon size={18} /><span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#000000] font-['Playfair_Display']">Factures</h1>
              <p className="text-sm text-[#6B7280] mt-1">Gestion et export des factures</p>
            </div>
            <Button
              onClick={exportMonthlyCSV}
              disabled={invoices.length === 0}
              className="bg-[#000000] hover:bg-[#1a1a1a] text-white gap-2"
              data-testid="export-csv-btn"
            >
              <FileCsv size={18} />
              Export CSV du mois
            </Button>
          </div>

          {/* Month Navigator */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="outline" size="sm" onClick={() => changeMonth(-1)} className="border-[#E2E8F0]">
              <CaretLeft size={16} />
            </Button>
            <h2 className="text-lg font-semibold capitalize text-[#000000]">{monthLabel}</h2>
            <Button variant="outline" size="sm" onClick={() => changeMonth(1)} className="border-[#E2E8F0]">
              <CaretRight size={16} />
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs text-[#6B7280] font-medium">Factures</p>
                <p className="text-2xl font-bold text-[#000000] mt-1">{invoices.length}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs text-[#6B7280] font-medium">Total HT</p>
                <p className="text-2xl font-bold text-[#000000] mt-1">{totalHT.toFixed(2)}€</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs text-[#6B7280] font-medium">TVA (20%)</p>
                <p className="text-2xl font-bold text-[#6B7280] mt-1">{totalTVA.toFixed(2)}€</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-[#000000]">
              <CardContent className="p-4">
                <p className="text-xs text-[#b7ede0] font-medium">Total TTC</p>
                <p className="text-2xl font-bold text-[#b7ede0] mt-1">{totalTTC.toFixed(2)}€</p>
              </CardContent>
            </Card>
          </div>

          {/* Invoice List */}
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner" /></div>
          ) : invoices.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center">
                <FileText size={48} className="mx-auto text-[#D1D5DB] mb-4" />
                <p className="text-[#6B7280] text-lg">Aucune facture pour ce mois</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]" data-testid="invoices-table">
                  <thead>
                    <tr className="bg-[#000000] text-[#b7ede0]">
                      <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider">N° Facture</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider">Sortie</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider">Passagers</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold tracking-wider">Total TTC</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold tracking-wider">Paiement</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold tracking-wider">PDF</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {invoices.map((inv) => {
                      const participants = inv.participants || [];
                      const clientName = participants.length > 0 ? `${participants[0].first_name} ${participants[0].last_name}` : '-';
                      const isOnline = inv.payment_type !== 'on_site';
                      return (
                        <tr key={inv.id} className="hover:bg-[#f8faf9] transition-colors" data-testid={`invoice-row-${inv.invoice_number}`}>
                          <td className="px-4 py-3">
                            <span className="font-mono font-semibold text-sm text-[#000000]">{inv.invoice_number}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-[#6B7280]">{inv.booking_date}</td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-[#000000]">{clientName}</div>
                            <div className="text-xs text-[#9CA3AF]">{inv.customer_email}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-[#4B5563]">{inv.trip_name}</td>
                          <td className="px-4 py-3 text-sm text-[#4B5563]">{participants.length}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-bold text-[#000000]">{inv.total_amount?.toFixed(2)}€</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={isOnline ? 'bg-[#ECFDF5] text-[#065F46] border-0' : 'bg-[#FFF7ED] text-[#9A3412] border-0'}>
                              {isOnline ? 'En ligne' : 'Sur place'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadInvoice(inv.id, inv.invoice_number)}
                              className="text-[#b7ede0] hover:text-[#000000] hover:bg-[#b7ede0]/20"
                              data-testid={`download-invoice-${inv.invoice_number}`}
                            >
                              <Download size={18} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminInvoicesPage;
